import React from 'react';
import Icon from '../../../components/AppIcon';


const CalendarGrid = ({ 
  currentDate, 
  viewMode, 
  shifts, 
  onShiftClick, 
  onDateClick,
  selectedDate 
}) => {
  const today = new Date();

  const getShiftsForDate = (date) => {
    return shifts?.filter(shift => {
      const shiftDate = new Date(shift.startTime);
      return shiftDate?.toDateString() === date?.toDateString();
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      assigned: 'bg-blue-500 text-white',
      available: 'bg-green-500 text-white',
      requested: 'bg-yellow-500 text-white',
      unavailable: 'bg-gray-400 text-white'
    };
    return colors?.[status] || 'bg-gray-200 text-gray-800';
  };

  const formatTime = (dateTime) => {
    return new Date(dateTime)?.toLocaleTimeString('de-DE', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isToday = (date) => {
    return date?.toDateString() === today?.toDateString();
  };

  const isSelected = (date) => {
    return selectedDate && date?.toDateString() === selectedDate?.toDateString();
  };

  const renderMonthView = () => {
    const firstDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
    const lastDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
    const startDate = new Date(firstDayOfMonth);
    startDate?.setDate(startDate?.getDate() - firstDayOfMonth?.getDay());

    const days = [];
    const current = new Date(startDate);
    
    for (let i = 0; i < 42; i++) {
      days?.push(new Date(current));
      current?.setDate(current?.getDate() + 1);
    }

    const weekDays = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];

    return (
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        {/* Week Days Header */}
        <div className="grid grid-cols-7 border-b border-border">
          {weekDays?.map((day) => (
            <div key={day} className="p-3 text-sm font-medium text-muted-foreground text-center bg-muted">
              {day}
            </div>
          ))}
        </div>
        {/* Calendar Grid */}
        <div className="grid grid-cols-7">
          {days?.map((date, index) => {
            const dayShifts = getShiftsForDate(date);
            const isCurrentMonth = date?.getMonth() === currentDate?.getMonth();
            
            return (
              <div
                key={index}
                className={`
                  min-h-[120px] p-2 border-r border-b border-border cursor-pointer hover:bg-muted transition-colors
                  ${!isCurrentMonth ? 'bg-gray-50 opacity-60' : ''}
                  ${isToday(date) ? 'bg-blue-50' : ''}
                  ${isSelected(date) ? 'bg-accent' : ''}
                `}
                onClick={() => onDateClick(date)}
              >
                <div className={`
                  text-sm font-medium mb-2
                  ${!isCurrentMonth ? 'text-muted-foreground' : 'text-foreground'}
                  ${isToday(date) ? 'text-blue-700' : ''}
                `}>
                  {date?.getDate()}
                </div>
                <div className="space-y-1">
                  {dayShifts?.slice(0, 3)?.map((shift) => (
                    <div
                      key={shift?.id}
                      className={`
                        text-xs px-2 py-1 rounded cursor-pointer hover:opacity-80 transition-opacity
                        ${getStatusColor(shift?.status)}
                      `}
                      onClick={(e) => {
                        e?.stopPropagation();
                        onShiftClick(shift);
                      }}
                    >
                      <div className="font-medium truncate">{shift?.department}</div>
                      <div className="opacity-90">
                        {formatTime(shift?.startTime)} - {formatTime(shift?.endTime)}
                      </div>
                    </div>
                  ))}
                  
                  {dayShifts?.length > 3 && (
                    <div className="text-xs text-muted-foreground px-2">
                      +{dayShifts?.length - 3} weitere
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderWeekView = () => {
    const startOfWeek = new Date(currentDate);
    startOfWeek?.setDate(currentDate?.getDate() - currentDate?.getDay());
    
    const weekDays = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(startOfWeek);
      day?.setDate(startOfWeek?.getDate() + i);
      weekDays?.push(day);
    }

    const hours = Array.from({ length: 16 }, (_, i) => i + 6); // 6 AM to 10 PM

    return (
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        {/* Week Header */}
        <div className="grid grid-cols-8 border-b border-border">
          <div className="p-3 bg-muted"></div>
          {weekDays?.map((day) => (
            <div key={day?.toISOString()} className="p-3 text-center bg-muted">
              <div className="text-sm font-medium text-foreground">
                {day?.toLocaleDateString('de-DE', { weekday: 'short' })}
              </div>
              <div className={`text-lg font-semibold ${isToday(day) ? 'text-blue-700' : 'text-foreground'}`}>
                {day?.getDate()}
              </div>
            </div>
          ))}
        </div>
        {/* Time Grid */}
        <div className="grid grid-cols-8 max-h-96 overflow-y-auto">
          {hours?.map((hour) => (
            <React.Fragment key={hour}>
              <div className="p-2 text-xs text-muted-foreground border-r border-border bg-muted">
                {hour?.toString()?.padStart(2, '0')}:00
              </div>
              {weekDays?.map((day) => {
                const dayShifts = getShiftsForDate(day)?.filter(shift => {
                  const shiftHour = new Date(shift.startTime)?.getHours();
                  return shiftHour === hour;
                });

                return (
                  <div key={`${day?.toISOString()}-${hour}`} className="p-1 border-r border-b border-border min-h-[60px]">
                    {dayShifts?.map((shift) => (
                      <div
                        key={shift?.id}
                        className={`
                          text-xs px-2 py-1 rounded mb-1 cursor-pointer hover:opacity-80 transition-opacity
                          ${getStatusColor(shift?.status)}
                        `}
                        onClick={() => onShiftClick(shift)}
                      >
                        <div className="font-medium truncate">{shift?.department}</div>
                      </div>
                    ))}
                  </div>
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </div>
    );
  };

  const renderDayView = () => {
    const dayShifts = getShiftsForDate(currentDate);
    const hours = Array.from({ length: 16 }, (_, i) => i + 6); // 6 AM to 10 PM

    return (
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        {/* Day Header */}
        <div className="p-4 border-b border-border bg-muted">
          <h3 className="text-lg font-semibold text-foreground">
            {currentDate?.toLocaleDateString('de-DE', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </h3>
        </div>
        {/* Time Slots */}
        <div className="max-h-96 overflow-y-auto">
          {hours?.map((hour) => {
            const hourShifts = dayShifts?.filter(shift => {
              const shiftHour = new Date(shift.startTime)?.getHours();
              return shiftHour === hour;
            });

            return (
              <div key={hour} className="flex border-b border-border">
                <div className="w-20 p-3 text-sm text-muted-foreground bg-muted border-r border-border">
                  {hour?.toString()?.padStart(2, '0')}:00
                </div>
                <div className="flex-1 p-3 min-h-[80px]">
                  <div className="space-y-2">
                    {hourShifts?.map((shift) => (
                      <div
                        key={shift?.id}
                        className={`
                          p-3 rounded-lg cursor-pointer hover:opacity-80 transition-opacity
                          ${getStatusColor(shift?.status)}
                        `}
                        onClick={() => onShiftClick(shift)}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-medium">{shift?.department}</div>
                            <div className="text-sm opacity-90">
                              {formatTime(shift?.startTime)} - {formatTime(shift?.endTime)}
                            </div>
                          </div>
                          <Icon name="ChevronRight" size={16} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="flex-1">
      {viewMode === 'month' && renderMonthView()}
      {viewMode === 'week' && renderWeekView()}
      {viewMode === 'day' && renderDayView()}
    </div>
  );
};

export default CalendarGrid;